/**
 * StatCard Component
 * Reusable statistic card with trend indicators
 */
import React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

export interface StatCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number; // percentage or absolute
    trend: 'up' | 'down' | 'neutral';
    period?: string; // e.g., "vs last month"
  };
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;
  color?: 'blue' | 'green' | 'amber' | 'red' | 'indigo' | 'purple';
  subColor?: string; // Custom color override
  onClick?: () => void;
  loading?: boolean;
  className?: string;
}

const colorClasses = {
  blue: 'bg-blue-500',
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  indigo: 'bg-indigo-500',
  purple: 'bg-purple-500',
};

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  color = 'blue',
  subColor,
  onClick,
  loading = false,
  className = '',
}) => {
  const bgColor = subColor || colorClasses[color];
  const isClickable = !!onClick;

  if (loading) {
    return (
      <div className={`bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm animate-pulse ${className}`}>
        <div className="flex justify-between items-start mb-4">
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
          <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
        </div>
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-4"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all min-w-0 ${isClickable ? 'cursor-pointer hover:border-brand-300 dark:hover:border-brand-700 active:scale-[0.98]' : ''
        } ${className}`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      } : undefined}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white truncate">{value}</h3>
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg ${bgColor} text-white shadow-sm flex-shrink-0 ml-2`}>
            <Icon size={20} />
          </div>
        )}
      </div>
      {change && (
        <div className="mt-4 flex items-center text-sm">
          {change.trend === 'up' ? (
            <span className="text-emerald-600 dark:text-emerald-400 flex items-center font-bold bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded">
              <ArrowUpRight size={16} className="mr-1" /> {change.value}%
            </span>
          ) : change.trend === 'down' ? (
            <span className="text-rose-600 dark:text-rose-400 flex items-center font-bold bg-rose-50 dark:bg-rose-900/20 px-1.5 py-0.5 rounded">
              <ArrowDownRight size={16} className="mr-1" /> {change.value}%
            </span>
          ) : (
            <span className="text-slate-600 dark:text-slate-400 flex items-center font-bold bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded">
              <Minus size={16} className="mr-1" /> {change.value}%
            </span>
          )}
          {change.period && (
            <span className="text-slate-400 dark:text-slate-500 ml-2">{change.period}</span>
          )}
        </div>
      )}
    </div>
  );
};

